// Output schema for cleansed/looked-up rows. The set of columns is fixed but
// varies slightly by mode (Confidence Score is cleanse-only) so callers must
// pass the mode when building headers or mapping a result.

import type { CleanseResult } from "./api";

export type ProcessingMode = "cleanse" | "uprn";

export function buildApiHeaders(mode: ProcessingMode): string[] {
  const headers = ["Ideal Postcodes Results", "Message"];
  if (mode === "cleanse") headers.push("Confidence Score");
  headers.push(
    "Organisation Name",
    "Address Line 1",
    "Address Line 2",
    "Address Line 3",
    "Post Town",
    "Postcode",
    "County",
    "Country",
    "UPRN",
    "UDPRN",
    "Longitude",
    "Latitude",
    "Eastings",
    "Northings",
    "Postcode Type",
    "SU Organisation Indicator",
    "UMPRN"
  );
  return headers;
}

// Compose original CSV columns with the API columns, deduping any names that
// already exist in the CSV (so a user-supplied "Postcode" column is preserved
// rather than appearing twice).
export function buildOutputHeaders(
  csvHeaders: string[],
  apiHeaders: string[]
): string[] {
  const extras = apiHeaders.filter((h) => !csvHeaders.includes(h));
  return [...csvHeaders, ...extras];
}

// Confidence comes through at either the root or nested under result depending
// on the API response shape; UPRN lookups never have one. Falsy values
// (including a 0 score) fall through and render as an empty string — matches
// the original behaviour where 0 was treated as "no match" rather than a
// genuine score.
function pickConfidence(res: CleanseResult, mode: ProcessingMode): string {
  if (mode !== "cleanse") return "";
  if (res.result?.confidence) return Number(res.result.confidence).toFixed(2);
  if (res.confidence) return Number(res.confidence).toFixed(2);
  return "";
}

// Map a raw API result onto the user's row, leaving original CSV columns in
// place and appending the standardised cleanse columns.
export function mapResultToRow(
  originalRow: Record<string, any>,
  res: CleanseResult,
  mode: ProcessingMode
): Record<string, any> {
  const match = mode === "cleanse" ? res.result?.match || {} : res.result || {};

  // In UPRN lookup mode, preserve the original UPRN value from the CSV
  // instead of overwriting it with the API response (which may be empty)
  const uprnValue = mode === "uprn" && originalRow.UPRN 
    ? originalRow.UPRN 
    : (match.uprn ?? "");

  return {
    ...originalRow,
    "Ideal Postcodes Results": "",
    Message: res.message ?? "",
    "Confidence Score": pickConfidence(res, mode),
    "Organisation Name": match.organisation_name ?? "",
    "Address Line 1": match.line_1 ?? "",
    "Address Line 2": match.line_2 ?? "",
    "Address Line 3": match.line_3 ?? "",
    "Post Town": match.post_town ?? "",
    Postcode: match.postcode ?? "",
    County: match.county ?? "",
    Country: match.country ?? "",
    UPRN: uprnValue,
    UDPRN: match.udprn ?? "",
    Longitude: match.longitude ?? "",
    Latitude: match.latitude ?? "",
    Eastings: match.eastings ?? "",
    Northings: match.northings ?? "",
    "Postcode Type": match.postcode_type ?? "",
    "SU Organisation Indicator": match.su_organisation_indicator ?? "",
    UMPRN: match.umprn ?? "",
    error: "",
  };
}

// Row shape for an entry whose API call failed. All API columns get empty
// strings so the CSV stays rectangular; the error message lands in `error`.
export function mapErrorToRow(
  originalRow: Record<string, any>,
  message: string,
  apiHeaders: string[]
): Record<string, any> {
  const out: Record<string, any> = { ...originalRow };
  for (const k of apiHeaders) {
    if (!(k in out)) out[k] = "";
  }
  out.error = message;
  return out;
}
